const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const MONTH_NAME_TO_NUM = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const PRESENT_DATE_REGEX = /^(present|current|now|ongoing|till\s*date)$/i;
const MONTH_NAME_PATTERN =
  'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';

export function isPresentDate(value) {
  return PRESENT_DATE_REGEX.test(String(value || '').trim());
}

function parseTwoDigitYear(yy) {
  const n = parseInt(yy, 10);
  if (!Number.isFinite(n)) return NaN;
  return n <= 30 ? 2000 + n : 1900 + n;
}

function parseMonthYear(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw || isPresentDate(raw)) return null;

  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) {
    const month = parseInt(m[2], 10);
    if (month >= 1 && month <= 12) return { year: parseInt(m[1], 10), month };
  }

  m = raw.match(/^(\d{4})[/\-](\d{1,2})$/);
  if (m) {
    const month = parseInt(m[2], 10);
    if (month >= 1 && month <= 12) return { year: parseInt(m[1], 10), month };
  }

  m = raw.match(/^(\d{1,2})[/\-](\d{4})$/);
  if (m) {
    const month = parseInt(m[1], 10);
    if (month >= 1 && month <= 12) return { year: parseInt(m[2], 10), month };
  }

  m = raw.match(new RegExp(`^(${MONTH_NAME_PATTERN})[\\s.'’\`]*(\\d{4}|\\d{2})$`, 'i'));
  if (m) {
    const month = MONTH_NAME_TO_NUM[m[1].toLowerCase()];
    const year = m[2].length === 2 ? parseTwoDigitYear(m[2]) : parseInt(m[2], 10);
    if (month && Number.isFinite(year)) return { year, month };
  }

  m = raw.match(/^(\d{4})$/);
  if (m) return { year: parseInt(m[1], 10), month: 1 };

  return null;
}

/** Value for <input type="month">. Present / empty / unparseable → ''. */
export function toMonthInputValue(value) {
  if (!value || isPresentDate(value)) return '';
  const parsed = parseMonthYear(value);
  if (!parsed) return '';
  const year = String(parsed.year).replace(/\D/g, '').slice(0, 4);
  if (year.length !== 4) return '';
  return `${year}-${String(parsed.month).padStart(2, '0')}`;
}

/**
 * Keep typed month values at YYYY-MM with a 4-digit year.
 * Leaves Present / empty unchanged. Extra year digits are dropped.
 */
export function clampMonthYearValue(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (isPresentDate(raw)) return 'Present';

  const match = raw.match(/^(\d+)-(\d{1,2})(?:-\d{1,2})?$/);
  if (match) {
    const year = match[1].slice(0, 4);
    if (year.length < 4) return '';
    const monthNum = parseInt(match[2], 10);
    const month = String(Math.min(12, Math.max(1, Number.isFinite(monthNum) ? monthNum : 1))).padStart(2, '0');
    return `${year}-${month}`;
  }

  if (/^\d+$/.test(raw)) return raw.slice(0, 4);

  return toMonthInputValue(raw);
}

/**
 * Display month + year (e.g. Jan 2020). Present stays Present.
 * Unparseable values return the original string — never "Invalid Date".
 */
export function formatExperienceDate(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (isPresentDate(raw)) return 'Present';
  const parsed = parseMonthYear(raw);
  if (!parsed) return raw;
  return `${MONTH_SHORT[parsed.month - 1]} ${parsed.year}`;
}
